import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/bi/AppShell";
import { EmptyState } from "@/components/bi/EmptyState";
import { Loading } from "@/components/bi/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { Database, Upload, Plus, Trash2, Table2, Loader2, FileSpreadsheet, Server, Compass, Boxes, Zap, Info, RefreshCw, Settings2, X, Pencil, Wand2, GitBranch, ShieldCheck, Lock } from "lucide-react";
import { LineageDialog } from "@/components/bi/LineageDialog";
import { ConnectionsPanel } from "@/components/bi/ConnectionsPanel";
import { DatasetCardActions, DatasetStatusDot } from "@/components/bi/DatasetCardActions";
import { useAuth } from "@/context/AuthContext";
import { isCrossTenant } from "@/lib/roles";

export default function DataSources() {
  const nav = useNavigate();
  const { user } = useAuth();
  const staff = isCrossTenant(user?.role);
  const [canAddSources, setCanAddSources] = useState(true);
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [dbOpen, setDbOpen] = useState(false);
  const [liveOpen, setLiveOpen] = useState(false);
  const [settingsDs, setSettingsDs] = useState(null);
  const [editDs, setEditDs] = useState(null);
  const [lineageDs, setLineageDs] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [statusKey, setStatusKey] = useState(0);

  const load = async () => {
    try { const { data } = await api.get("/datasets"); setItems(data); }
    catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!user?.tenant_id) return;
    api.get(`/tenants/${user.tenant_id}/source-policy`)
      .then(({ data }) => setCanAddSources(data.allow_tenant_data_sources !== false)).catch(() => {});
  }, [user?.tenant_id]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/datasets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported “${data.name}” (${data.row_count} rows)`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };


  const refresh = async (id) => {
    setRefreshingId(id);
    try {
      const { data } = await api.post(`/datasets/${id}/refresh`);
      if (data.status === "error") toast.error(`Refresh failed — ${data.message}`);
      else toast.success(data.rows != null ? `Refreshed — ${data.rows.toLocaleString()} rows re-read from the source` : `Cache refreshed — ${data.invalidated} cached result${data.invalidated === 1 ? "" : "s"} cleared`);
      setStatusKey((k) => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    finally { setRefreshingId(null); }
  };

  const actions = (
    <div className="flex gap-2 items-center">
      {!canAddSources && !staff && (
        <span data-testid="sources-locked-note" className="hidden md:flex items-center gap-1 text-[11px] text-[color:var(--ink-3)] max-w-[260px] leading-snug">
          <Lock className="w-3 h-3 shrink-0" />Your Eazy administrator provisions the data sources for this workspace.
        </span>
      )}
      <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.json" className="hidden" onChange={onFile} data-testid="upload-file-input" />
      <Button data-testid="upload-btn" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading || (!canAddSources && !staff)} className="bg-[color:var(--surface)]">
        {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}Upload file
      </Button>
      <Button data-testid="connect-db-btn" variant="outline" disabled={!canAddSources && !staff} onClick={() => setDbOpen(true)} className="bg-[color:var(--surface)]"><Server className="w-4 h-4 mr-1" />Import database</Button>
      <Button data-testid="live-connect-btn" disabled={!canAddSources && !staff} onClick={() => setLiveOpen(true)} className="bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white"><Zap className="w-4 h-4 mr-1" />Live connection</Button>
    </div>
  );

  return (
    <AppShell title="Data Sources" actions={actions}>
      <div className="px-5 md:px-8 py-6">
        {items === null ? <Loading label="Loading datasets" /> :
          items.length === 0 ? (
            <EmptyState testid="datasets-empty" icon={Database} title="No datasets yet"
              description="Upload a CSV, Excel, or JSON file, or connect to PostgreSQL, MySQL, Supabase, or MongoDB to get started."
              action={<div className="flex gap-2">
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[color:var(--surface)]"><Upload className="w-4 h-4 mr-1" />Upload file</Button>
                <Button onClick={() => setDbOpen(true)} className="bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white"><Server className="w-4 h-4 mr-1" />Connect database</Button>
              </div>} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((ds) => (
                <div key={ds.id} data-testid="dataset-card" className="glass-card p-5">
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl hero-gradient flex items-center justify-center border border-[color:var(--glass-border)]">
                      {ds.mode === "live" ? <Zap className="w-5 h-5 text-[color:var(--brand-ocean)]" /> : ds.source === "database" ? <Server className="w-5 h-5 text-[color:var(--brand-ocean)]" /> : <FileSpreadsheet className="w-5 h-5 text-[color:var(--brand-ocean)]" />}
                    </div>
                    {ds.mode === "live"
                      ? <Badge data-testid="live-badge" className="bg-[color:rgba(0,194,168,0.16)] text-[color:var(--success)] border-0"><Zap className="w-3 h-3 mr-1" />Live · {ds.db_type}</Badge>
                      : <Badge variant="outline" className="text-[color:var(--ink-3)]">{ds.source}</Badge>}
                  </div>
                  {ds.managed_source && (
                    <Badge data-testid="managed-badge" variant="outline" className="mt-3 text-[10px] text-[color:var(--brand-ocean)] border-[color:var(--brand-ocean)]"
                      title={`${ds.managed_source.label} · provisioned by ${ds.managed_source.by || "Eazy"}`}>
                      <ShieldCheck className="w-3 h-3 mr-1" />Managed by Eazy
                    </Badge>
                  )}
                  <h3 className="font-display text-lg text-[color:var(--ink)] mt-4 truncate">{ds.name}</h3>
                  <div className="text-xs text-[color:var(--ink-3)] mt-1 flex items-center gap-2 flex-wrap">
                    <span>{ds.mode === "live" ? <>Live source · {ds.column_count} cols</> : <>{ds.row_count?.toLocaleString()} rows · {ds.column_count} cols</>}</span>
                    <DatasetStatusDot ds={ds} refreshKey={statusKey} />
                    {ds.my_role && ds.my_role !== "owner" && <Badge variant="outline" className="text-[9px] py-0 text-[color:var(--ink-3)]">{ds.my_role === "admin" ? "admin" : `shared · ${ds.my_role}`}</Badge>}
                    {ds.shared_count > 0 && <span className="text-[10px]" title={`Shared with ${ds.shared_count} user(s)`}>· {ds.shared_count} shared</span>}
                    {ds.mode === "live" && (
                      <button data-testid="ttl-chip" onClick={() => setSettingsDs(ds)} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--surface-2)] text-[color:var(--ink-2)] hover:text-[color:var(--brand-ocean)] transition-colors" title="Edit cache TTL">
                        <RefreshCw className="w-2.5 h-2.5" />cache {ds.cache_ttl_seconds ?? 300}s
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-3">
                    {(ds.schema || []).slice(0, 4).map((c) => (
                      <span key={c.name} className="text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--surface-2)] text-[color:var(--ink-3)]">{c.name}</span>
                    ))}
                    {(ds.schema || []).length > 4 && <span className="text-[10px] px-2 py-0.5 text-[color:var(--ink-3)]">+{ds.schema.length - 4}</span>}
                  </div>
                  <div className="mt-4 space-y-2">
                    {/* Primary actions — two equal columns that always fit */}
                    <div className="grid grid-cols-2 gap-2">
                      <Button size="sm" variant="outline" className="bg-[color:var(--surface)] min-w-0" onClick={() => nav(`/explore/${ds.id}`)}><Compass className="w-4 h-4 mr-1 shrink-0" /><span className="truncate">Explore</span></Button>
                      <Button data-testid="model-btn" size="sm" variant="outline" className="bg-[color:var(--surface)] min-w-0" onClick={() => nav(`/model/${ds.id}`)}><Boxes className="w-4 h-4 mr-1 shrink-0" /><span className="truncate">Model</span></Button>
                    </div>
                    {/* Secondary icon actions — wrap onto extra rows on narrow tiles instead of overflowing the card */}
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Button data-testid="prep-btn" size="sm" variant="ghost" onClick={() => nav(`/prep/${ds.id}`)} className="text-[color:var(--brand-mint)] hover:bg-[color:rgba(0,194,168,0.08)] h-8 w-8 p-0" title="Data prep (ETL steps)">
                        <Wand2 className="w-4 h-4" />
                      </Button>
                      <Button data-testid="lineage-btn" size="sm" variant="ghost" onClick={() => setLineageDs(ds)} className="text-[color:var(--brand-ocean)] hover:bg-[color:rgba(43,78,255,0.08)] h-8 w-8 p-0" title="Lineage & impact analysis">
                        <GitBranch className="w-4 h-4" />
                      </Button>
                      {ds.mode === "live" && (
                        <>
                          <Button data-testid="edit-query-btn" size="sm" variant="ghost" onClick={() => setEditDs(ds)} className="text-[color:var(--ink-2)] hover:text-[color:var(--brand-ocean)] h-8 w-8 p-0" title="Edit live query">
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button data-testid="refresh-btn" size="sm" variant="ghost" onClick={() => refresh(ds.id)} disabled={refreshingId === ds.id} className="text-[color:var(--brand-ocean)] hover:bg-[color:rgba(23,112,138,0.08)] h-8 w-8 p-0" title={ds.mode === "live" ? "Refresh now (re-read from source)" : "Refresh now (clear cached results)"}>
                        <RefreshCw className={"w-4 h-4 " + (refreshingId === ds.id ? "animate-spin" : "")} />
                      </Button>
                      <span className="ml-auto"><DatasetCardActions ds={ds} onChanged={load} onDeleted={load} /></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        <ConnectionsPanel />
      </div>
      <ConnectDBDialog open={dbOpen} onOpenChange={setDbOpen} onImported={load} />
      <LiveDatasetDialog open={liveOpen || !!editDs} onOpenChange={(v) => { if (!v) { setLiveOpen(false); setEditDs(null); } else setLiveOpen(true); }} onCreated={load} editDs={editDs} />
      <LiveDatasetSettings ds={settingsDs} onOpenChange={(v) => { if (!v) setSettingsDs(null); }} onSaved={load} />
      <LineageDialog open={!!lineageDs} onOpenChange={(v) => { if (!v) setLineageDs(null); }} dataset={lineageDs} />
    </AppShell>
  );
}

function LiveDatasetSettings({ ds, onOpenChange, onSaved }) {
  const [name, setName] = useState("");
  const [ttl, setTtl] = useState(300);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (ds) { setName(ds.name || ""); setTtl(ds.cache_ttl_seconds ?? 300); } }, [ds]);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/datasets/${ds.id}`, { name: name.trim(), cache_ttl_seconds: Number(ttl) });
      toast.success("Live dataset updated");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ds} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Settings2 className="w-5 h-5 text-[color:var(--brand-ocean)]" />Live dataset settings</DialogTitle>
          <DialogDescription>Rename the dataset and control how long its live results are cached.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div><Label>Dataset name</Label><Input data-testid="settings-name" className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div>
            <Label>Cache TTL (seconds)</Label>
            <Input data-testid="settings-ttl" type="number" min={5} max={86400} className="mt-1.5" value={ttl} onChange={(e) => setTtl(e.target.value)} />
            <p className="text-[11px] text-[color:var(--ink-3)] mt-1.5">How long live query results are cached before re-fetching from the source. Range 5–86400s. Use “Refresh now” to bust the cache immediately.</p>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="settings-save" onClick={save} disabled={busy || !name.trim()} className="bg-[color:var(--brand-ocean)] text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ConnectDBDialog({ open, onOpenChange, onImported }) {
  const [form, setForm] = useState({ db_type: "postgres", host: "", port: "", database: "", username: "", password: "", project_url: "", api_key: "", uri: "" });
  const [tables, setTables] = useState([]);
  const [table, setTable] = useState("");
  const [testing, setTesting] = useState(false);
  const [importing, setImporting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setType = (v) => { setForm((f) => ({ ...f, db_type: v })); setTables([]); setTable(""); };
  const isMongo = form.db_type === "mongodb";

  const test = async () => {
    setTesting(true); setTables([]);
    try {
      const payload = { ...form, port: form.port ? Number(form.port) : null };
      const { data } = await api.post("/datasets/db/test", payload);
      setTables(data.tables || []);
      toast.success(`Connected — ${data.tables.length} tables found`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Connection failed"); }
    finally { setTesting(false); }
  };
  const doImport = async () => {
    if (!table) { toast.error("Pick a table"); return; }
    setImporting(true);
    try {
      const payload = { ...form, port: form.port ? Number(form.port) : null, table };
      const { data } = await api.post("/datasets/db/import", payload);
      toast.success(`Imported “${data.name}”`);
      onOpenChange(false); onImported();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Connect a database</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label>Source type</Label>
            <Select value={form.db_type} onValueChange={setType}>
              <SelectTrigger data-testid="db-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="postgres">PostgreSQL</SelectItem>
                <SelectItem value="mysql">MySQL</SelectItem>
                <SelectItem value="supabase">Supabase</SelectItem>
                <SelectItem value="mongodb">MongoDB</SelectItem>
                <SelectItem value="sqlserver">SQL Server (beta)</SelectItem>
                <SelectItem value="oracle">Oracle (beta)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(form.db_type === "postgres" || form.db_type === "mysql" || form.db_type === "sqlserver" || form.db_type === "oracle") && (
            <>
              {(form.db_type === "sqlserver" || form.db_type === "oracle") && (
                <p data-testid="beta-connector-note" className="text-[11px] rounded-lg border border-[color:rgba(245,158,11,0.35)] bg-[color:rgba(245,158,11,0.10)] p-2 text-[color:var(--ink-2)]">
                  <b>Beta connector</b> — driver installed but not yet validated against a live {form.db_type === "oracle" ? "Oracle" : "SQL Server"} instance. {form.db_type === "oracle" ? "Enter the service name in the Database field." : ""}
                </p>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><Label>Host</Label><Input data-testid="db-host" className="mt-1.5" placeholder="db.example.com" value={form.host} onChange={(e) => set("host", e.target.value)} /></div>
                <div><Label>Port</Label><Input className="mt-1.5" placeholder={{ mysql: "3306", sqlserver: "1433", oracle: "1521" }[form.db_type] || "5432"} value={form.port} onChange={(e) => set("port", e.target.value)} /></div>
              </div>
              <div><Label>{form.db_type === "oracle" ? "Service name" : "Database"}</Label><Input data-testid="db-name" className="mt-1.5" value={form.database} onChange={(e) => set("database", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Username</Label><Input className="mt-1.5" value={form.username} onChange={(e) => set("username", e.target.value)} /></div>
                <div><Label>Password</Label><Input className="mt-1.5" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} /></div>
              </div>
            </>
          )}

          {form.db_type === "supabase" && (
            <>
              <div><Label>Project URL</Label><Input data-testid="sb-url" className="mt-1.5" placeholder="https://xxxx.supabase.co" value={form.project_url} onChange={(e) => set("project_url", e.target.value)} /></div>
              <div><Label>API key</Label><Input data-testid="sb-key" type="password" className="mt-1.5" placeholder="anon or service_role key" value={form.api_key} onChange={(e) => set("api_key", e.target.value)} /></div>
              <p className="text-[11px] text-[color:var(--ink-3)]">From your Supabase dashboard → Project Settings → API. Tables are read via the PostgREST Data API (row-level security applies for anon keys).</p>
            </>
          )}

          {form.db_type === "mongodb" && (
            <>
              <div><Label>Connection string</Label><Input data-testid="mongo-uri" type="password" className="mt-1.5" placeholder="mongodb+srv://user:pass@cluster.mongodb.net" value={form.uri} onChange={(e) => set("uri", e.target.value)} /></div>
              <div><Label>Database</Label><Input data-testid="mongo-db" className="mt-1.5" placeholder="my_database" value={form.database} onChange={(e) => set("database", e.target.value)} /></div>
            </>
          )}

          <Button data-testid="db-test-btn" variant="outline" onClick={test} disabled={testing} className="w-full bg-[color:var(--surface)]">{testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Server className="w-4 h-4 mr-1" />}Test connection</Button>
          {tables.length > 0 && (
            <div><Label>{isMongo ? "Collection to import" : "Table to import"}</Label>
              <Select value={table} onValueChange={setTable}>
                <SelectTrigger data-testid="db-table-select" className="mt-1.5"><SelectValue placeholder={isMongo ? "Select a collection" : "Select a table"} /></SelectTrigger>
                <SelectContent>{tables.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="db-import-btn" onClick={doImport} disabled={importing || !table} className="bg-[color:var(--brand-ocean)] text-white">{importing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Table2 className="w-4 h-4 mr-1" />}Import table</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SOURCE_LABELS = {
  postgres: "PostgreSQL", mysql: "MySQL", supabase: "Supabase (direct Postgres)", mongodb: "MongoDB",
  sqlserver: "SQL Server (beta)", oracle: "Oracle (beta)",
  snowflake: "Snowflake", bigquery: "Google BigQuery", redshift: "Amazon Redshift",
  databricks: "Databricks SQL", synapse: "Azure Synapse / SQL", athena: "AWS Athena (S3 lake)",
  gsheet: "Google Sheets", rest: "REST / JSON API",
};
// Which common credential rows apply per connector type
const NO_HOST_TYPES = ["mongodb", "snowflake", "bigquery", "athena", "gsheet", "rest"];
const NO_DB_TYPES = ["mongodb", "bigquery", "databricks", "gsheet", "rest"];
const NO_AUTH_TYPES = ["mongodb", "bigquery", "databricks", "athena", "gsheet", "rest"];
const CONN_STR_TYPES = ["postgres", "supabase", "mysql", "sqlserver", "oracle", "redshift", "synapse"];
const DEFAULT_PORTS = { mysql: "3306", sqlserver: "1433", synapse: "1433", oracle: "1521", redshift: "5439" };
// Connector-specific fields rendered from config (stored in the encrypted secret via `params`)
const CONNECTOR_FIELDS = {
  snowflake: [
    { k: "account", label: "Account identifier", ph: "ab12345.us-east-1" },
    { k: "warehouse", label: "Warehouse", ph: "COMPUTE_WH" },
    { k: "schema", label: "Schema (optional)", ph: "PUBLIC" },
    { k: "role", label: "Role (optional)", ph: "SYSADMIN" },
  ],
  bigquery: [
    { k: "project", label: "GCP project id", ph: "my-gcp-project" },
    { k: "dataset", label: "Dataset (optional)", ph: "analytics" },
    { k: "credentials_json", label: "Service-account JSON key", ph: '{ "type": "service_account", … }', textarea: true,
      hint: "IAM → Service Accounts → create key (JSON). Grant BigQuery Data Viewer + Job User." },
  ],
  databricks: [
    { k: "http_path", label: "HTTP path", ph: "/sql/1.0/warehouses/abc123def456" },
    { k: "access_token", label: "Access token", ph: "dapi…", secret: true },
    { k: "catalog", label: "Catalog (optional)", ph: "main" },
    { k: "schema", label: "Schema (optional)", ph: "default" },
  ],
  athena: [
    { k: "region", label: "AWS region", ph: "us-east-1" },
    { k: "s3_staging_dir", label: "S3 staging directory", ph: "s3://my-bucket/athena-results/" },
    { k: "aws_access_key_id", label: "Access key ID", ph: "AKIA…" },
    { k: "aws_secret_access_key", label: "Secret access key", secret: true },
    { k: "workgroup", label: "Workgroup (optional)", ph: "primary" },
  ],
  gsheet: [
    { k: "sheet_url", label: "Google Sheet URL or ID", ph: "https://docs.google.com/spreadsheets/d/…" },
    { k: "auth_mode", label: "Auth method", select: [["service_account", "Service-account JSON"], ["oauth", "OAuth 2.0 (auto-refresh)"]], def: "service_account" },
    { k: "credentials_json", label: "Service-account JSON key", ph: '{ "type": "service_account", … }', textarea: true,
      hint: "Share the sheet (Viewer) with the service-account email from the JSON key.",
      showIf: (p) => (p.auth_mode || "service_account") === "service_account" },
    { k: "client_id", label: "OAuth client ID", ph: "1234-abc.apps.googleusercontent.com", showIf: (p) => p.auth_mode === "oauth" },
    { k: "client_secret", label: "OAuth client secret", secret: true, showIf: (p) => p.auth_mode === "oauth" },
    { k: "refresh_token", label: "Refresh token", secret: true, showIf: (p) => p.auth_mode === "oauth",
      hint: "From Google OAuth Playground (Sheets read-only scope) using your client ID/secret. Access tokens are refreshed automatically." },
  ],
  rest: [
    { k: "url", label: "API URL", ph: "https://api.example.com/v1/orders" },
    { k: "auth_mode", label: "Auth", select: [["none", "None"], ["bearer", "Bearer token"], ["oauth2", "OAuth 2.0 (auto-refresh)"]], def: "none" },
    { k: "auth_token", label: "Bearer token", secret: true, showIf: (p) => (p.auth_mode || "none") === "bearer" },
    { k: "token_url", label: "Token URL", ph: "https://auth.example.com/oauth/token", showIf: (p) => p.auth_mode === "oauth2" },
    { k: "client_id", label: "Client ID", showIf: (p) => p.auth_mode === "oauth2" },
    { k: "client_secret", label: "Client secret", secret: true, showIf: (p) => p.auth_mode === "oauth2" },
    { k: "refresh_token", label: "Refresh token (optional)", secret: true, showIf: (p) => p.auth_mode === "oauth2",
      hint: "Provided → refresh_token grant; empty → client-credentials grant. Expired access tokens auto-refresh." },
    { k: "scope", label: "Scope (optional)", ph: "read:orders", showIf: (p) => p.auth_mode === "oauth2" },
    { k: "headers", label: "Headers JSON (optional)", ph: '{ "X-Api-Key": "…" }', textarea: true },
    { k: "json_path", label: "JSON path to rows (optional)", ph: "data.items" },
  ],
};
const CONNECTOR_HOST_META = {
  databricks: { label: "Server hostname", ph: "adb-123456789.1.azuredatabricks.net" },
  redshift: { label: "Cluster host", ph: "my-cluster.abc123.us-east-1.redshift.amazonaws.com" },
  synapse: { label: "Server", ph: "myworkspace.sql.azuresynapse.net" },
};
const FILTER_OPS = ["=", "!=", ">", "<", ">=", "<=", "like", "ilike", "in", "not in", "between", "is null", "is not null"];
const AGG_FUNCS = ["sum", "avg", "count", "count_distinct", "min", "max"];

function ConnectorExtraFields({ type, params, setP, idPrefix }) {
  const fields = CONNECTOR_FIELDS[type] || [];
  if (!fields.length) return null;
  return (
    <div className="space-y-2.5">
      {fields.filter((f) => !f.showIf || f.showIf(params)).map((f) => (
        <div key={f.k}>
          <Label>{f.label}</Label>
          {f.select ? (
            <Select value={params[f.k] || f.def} onValueChange={(v) => setP(f.k, v)}>
              <SelectTrigger data-testid={`${idPrefix}-${f.k}`} className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent>{f.select.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          ) : f.textarea ? (
            <Textarea data-testid={`${idPrefix}-${f.k}`} className="mt-1.5 bg-[color:var(--surface)] font-mono text-xs" rows={3}
              placeholder={f.ph} value={params[f.k] || ""} onChange={(e) => setP(f.k, e.target.value)} />
          ) : (
            <Input data-testid={`${idPrefix}-${f.k}`} className="mt-1.5 bg-[color:var(--surface)]" type={f.secret ? "password" : "text"}
              placeholder={f.ph} value={params[f.k] || ""} onChange={(e) => setP(f.k, e.target.value)} />
          )}
          {f.hint && <p className="text-[10.5px] text-[color:var(--ink-3)] mt-1">{f.hint}</p>}
        </div>
      ))}
    </div>
  );
}

function NewConnectionForm({ onSaved }) {
  const [nc, setNc] = useState({ name: "", db_type: "postgres", host: "", port: "", database: "", username: "", password: "", connection_string: "", uri: "", params: {} });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const set = (k, v) => setNc((f) => (k === "db_type" ? { ...f, db_type: v, params: {} } : { ...f, [k]: v }));
  const setP = (k, v) => setNc((f) => ({ ...f, params: { ...(f.params || {}), [k]: v } }));
  const t = nc.db_type;
  const isMongo = t === "mongodb";
  const test = async () => {
    setTesting(true);
    try {
      const payload = { ...nc, port: nc.port ? Number(nc.port) : null };
      const { data } = await api.post("/datasets/db/test", payload);
      const n = (data.tables || []).length;
      toast.success(t === "gsheet" ? `Connection OK — ${n} worksheets found` : t === "rest" ? `API OK — ${n} columns detected` : `Connection OK — ${n} tables reachable`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Connection test failed"); }
    finally { setTesting(false); }
  };
  const save = async () => {
    if (!nc.name) { toast.error("Name the connection"); return; }
    setBusy(true);
    try {
      const payload = { ...nc, port: nc.port ? Number(nc.port) : null };
      const { data } = await api.post("/connections", payload);
      toast.success(`Connection “${data.name}” saved`);
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Connection failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-xl border border-[color:var(--glass-border)] p-3 bg-[color:var(--surface-2)]">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Connection name</Label><Input data-testid="live-conn-name" className="mt-1.5 bg-[color:var(--surface)]" value={nc.name} onChange={(e) => set("name", e.target.value)} /></div>
        <div><Label>Type</Label>
          <Select value={nc.db_type} onValueChange={(v) => set("db_type", v)}>
            <SelectTrigger data-testid="live-conn-type" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.keys(SOURCE_LABELS).map((k) => <SelectItem key={k} value={k}>{SOURCE_LABELS[k]}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {isMongo ? (
        <>
          <div><Label>Connection string</Label><Input data-testid="live-mongo-uri" type="password" className="mt-1.5 bg-[color:var(--surface)]" placeholder="mongodb+srv://user:pass@cluster" value={nc.uri} onChange={(e) => set("uri", e.target.value)} /></div>
          <div><Label>Database</Label><Input className="mt-1.5 bg-[color:var(--surface)]" value={nc.database} onChange={(e) => set("database", e.target.value)} /></div>
        </>
      ) : (
        <>
          {CONN_STR_TYPES.includes(t) && (
            <>
              <div><Label>Connection string (optional)</Label><Input data-testid="live-conn-string" type="password" className="mt-1.5 bg-[color:var(--surface)]" placeholder="postgresql://user:pass@host:5432/db" value={nc.connection_string} onChange={(e) => set("connection_string", e.target.value)} /></div>
              <p className="text-[11px] text-[color:var(--ink-3)]">…or fill the fields below. Supabase live needs the <b>direct Postgres</b> connection string (Settings → Database).</p>
            </>
          )}
          {t === "supabase" && (
            <div data-testid="supabase-pooler-hint" className="flex gap-2 rounded-lg border border-[color:rgba(245,158,11,0.35)] bg-[color:rgba(245,158,11,0.10)] p-2.5 text-[11px] leading-relaxed text-[color:var(--ink-2)]">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[color:var(--amber,#f59e0b)]" />
              <span>
                Use the <b>Session Pooler (IPv4)</b> connection string — the direct host
                (<code>db.&lt;ref&gt;.supabase.co</code>) is IPv6-only and unreachable from this server.
                In Supabase: <b>Project Settings → Database → Connection string → “Session pooler”</b>, e.g.
                <code className="break-all"> postgresql://postgres.&lt;ref&gt;:&lt;pw&gt;@aws-0-&lt;region&gt;.pooler.supabase.com:5432/postgres</code>
              </span>
            </div>
          )}
          {!NO_HOST_TYPES.includes(t) && (
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><Label>{CONNECTOR_HOST_META[t]?.label || "Host"}</Label><Input data-testid="live-conn-host" className="mt-1.5 bg-[color:var(--surface)]" placeholder={CONNECTOR_HOST_META[t]?.ph || ""} value={nc.host} onChange={(e) => set("host", e.target.value)} /></div>
              <div><Label>Port</Label><Input className="mt-1.5 bg-[color:var(--surface)]" placeholder={DEFAULT_PORTS[t] || "5432"} value={nc.port} onChange={(e) => set("port", e.target.value)} /></div>
            </div>
          )}
          <div className={NO_AUTH_TYPES.includes(t) ? "" : "grid grid-cols-3 gap-2"}>
            {!NO_DB_TYPES.includes(t) && (
              <div><Label>{t === "oracle" ? "Service name" : t === "athena" ? "Athena database" : "Database"}</Label><Input data-testid="live-conn-db" className="mt-1.5 bg-[color:var(--surface)]" placeholder={t === "athena" ? "default" : ""} value={nc.database} onChange={(e) => set("database", e.target.value)} /></div>
            )}
            {!NO_AUTH_TYPES.includes(t) && (
              <>
                <div><Label>User</Label><Input className="mt-1.5 bg-[color:var(--surface)]" value={nc.username} onChange={(e) => set("username", e.target.value)} /></div>
                <div><Label>Password</Label><Input type="password" className="mt-1.5 bg-[color:var(--surface)]" value={nc.password} onChange={(e) => set("password", e.target.value)} /></div>
              </>
            )}
          </div>
          <ConnectorExtraFields type={t} params={nc.params || {}} setP={setP} idPrefix="live-conn" />
          {(t === "snowflake" || t === "redshift" || t === "databricks" || t === "synapse" || t === "athena") && (
            <p className="text-[10.5px] text-[color:var(--ink-3)]">Cloud warehouse connections are live — queries run in {SOURCE_LABELS[t].replace(/ \(.*\)/, "")} and only results are transferred.</p>
          )}
        </>
      )}
      <div className="flex gap-2">
        <Button data-testid="live-conn-test" variant="outline" onClick={test} disabled={testing || busy} className="bg-[color:var(--surface)]">
          {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Server className="w-4 h-4 mr-1" />}Test
        </Button>
        <Button data-testid="live-conn-save" onClick={save} disabled={busy || testing} className="flex-1 bg-[color:var(--brand-ocean)] text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}Save connection</Button>
      </div>
    </div>
  );
}

function LiveDatasetDialog({ open, onOpenChange, onCreated, editDs }) {
  const isEdit = !!editDs;
  const [conns, setConns] = useState([]);
  const [connId, setConnId] = useState("");
  const [creating, setCreating] = useState(false);
  const [tables, setTables] = useState([]);
  const [mode, setMode] = useState("visual");
  const [table, setTable] = useState("");
  const [colsByTable, setColsByTable] = useState({});   // { tableName: [colName,...] }
  const [sql, setSql] = useState("");
  const [filterText, setFilterText] = useState("");
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [ttl, setTtl] = useState(300);
  // visual designer state
  const [joins, setJoins] = useState([]);               // [{type,table,left,right}]
  const [filters, setFilters] = useState([]);           // [{column,op,value}]
  const [groupBy, setGroupBy] = useState([]);           // [qualifiedCol]
  const [aggs, setAggs] = useState([]);                 // [{func,column,alias}]
  const [limit, setLimit] = useState(1000);

  const conn = conns.find((c) => c.id === connId);
  const srcType = conn?.db_type || (isEdit ? editDs.db_type : "");
  const isMongo = srcType === "mongodb";
  const isGsheet = srcType === "gsheet";
  const isRest = srcType === "rest";
  const hasJoins = joins.some((j) => j.table);
  const activeTables = [table, ...joins.map((j) => j.table)].filter(Boolean);
  const qualify = (t, c) => (hasJoins ? `${t}.${c}` : c);
  const qualifiedColumns = activeTables.flatMap((t) => (colsByTable[t] || []).map((c) => qualify(t, c)));

  const resetDesigner = () => { setJoins([]); setFilters([]); setGroupBy([]); setAggs([]); setLimit(1000); setColsByTable({}); };

  const loadConns = async () => { try { const { data } = await api.get("/connections"); setConns(data); } catch { setConns([]); } };

  const fetchCols = async (t, cid = connId) => {
    if (!t || colsByTable[t]) return;
    try { const { data } = await api.post(`/connections/${cid}/columns`, { table: t }); setColsByTable((m) => ({ ...m, [t]: (data.columns || []).map((c) => c.name) })); }
    catch { setColsByTable((m) => ({ ...m, [t]: [] })); }
  };

  const hydrateFromDs = (ds) => {
    const spec = ds.live_spec || {};
    setName(ds.name || ""); setTtl(ds.cache_ttl_seconds ?? 300); setPreview(null);
    resetDesigner(); setSql(""); setFilterText("");
    if (ds.db_type === "mongodb" || spec.query_type === "mongo") {
      setTable(spec.collection || "");
      setFilterText(spec.filter ? JSON.stringify(spec.filter) : "");
    } else if (ds.db_type === "gsheet" || spec.query_type === "gsheet") {
      setTable(spec.worksheet || "");
    } else if (ds.db_type === "rest" || spec.query_type === "rest") {
      setTable("");
    } else if (spec.designer) {
      const d = spec.designer;
      setMode("visual"); setTable(d.base_table || "");
      setJoins((d.joins || []).map((j) => ({ type: j.type || "inner", table: j.table || "", left: j.left || "", right: j.right || "", _k: crypto.randomUUID() })));
      setFilters((d.filters || []).map((f) => ({ column: f.column || "", op: f.op || "=", value: Array.isArray(f.value) ? f.value.join(", ") : (f.value ?? ""), _k: crypto.randomUUID() })));
      setGroupBy(d.dimensions || []);
      setAggs((d.aggregations || []).map((a) => ({ func: a.func || "sum", column: a.column === "*" ? "" : (a.column || ""), alias: a.alias || "" })));
      setLimit(d.limit || 1000);
      [d.base_table, ...(d.joins || []).map((j) => j.table)].filter(Boolean).forEach((t) => fetchCols(t, ds.connection_id));
    } else {
      setMode("sql"); setSql(spec.query || ""); setTable("");
    }
  };

  const skipResetRef = useRef(false);
  useEffect(() => {
    if (!open) return;
    loadConns(); setCreating(false); setPreview(null);
    if (editDs) {
      skipResetRef.current = true;
      setConnId(editDs.connection_id);
      loadTables(editDs.connection_id);
      hydrateFromDs(editDs);
    } else {
      skipResetRef.current = false;
      setConnId(""); setTables([]); setTable(""); setSql(""); setFilterText(""); setName(""); setTtl(300); setMode("visual"); resetDesigner();
    }
    // hydrateFromDs/loadTables are per-render helpers; effect is keyed by dialog open + target dataset
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editDs]);

  const loadTables = async (id) => { try { const { data } = await api.post(`/connections/${id}/tables`); setTables(data.tables || []); } catch (e) { toast.error(e?.response?.data?.detail || "Could not list tables"); } };
  useEffect(() => {
    if (skipResetRef.current) { skipResetRef.current = false; return; }
    if (connId) loadTables(connId);
    setTable(""); setPreview(null); resetDesigner();
  }, [connId]);

  const onPickTable = async (t) => {
    setTable(t); setGroupBy([]); setAggs([]); setFilters([]); setJoins([]);
    if (!isMongo) fetchCols(t);
  };

  const addJoin = () => setJoins((j) => [...j, { type: "inner", table: "", left: "", right: "", _k: crypto.randomUUID() }]);
  const updJoin = (i, k, v) => setJoins((j) => j.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const rmJoin = (i) => setJoins((j) => j.filter((_, idx) => idx !== i));
  const addFilter = () => setFilters((f) => [...f, { column: "", op: "=", value: "", _k: crypto.randomUUID() }]);
  const updFilter = (i, k, v) => setFilters((f) => f.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const rmFilter = (i) => setFilters((f) => f.filter((_, idx) => idx !== i));
  const addAgg = () => setAggs((a) => [...a, { func: "sum", column: "", alias: "", _k: crypto.randomUUID() }]);
  const updAgg = (i, k, v) => setAggs((a) => a.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const rmAgg = (i) => setAggs((a) => a.filter((_, idx) => idx !== i));
  const toggleGroup = (c) => setGroupBy((g) => g.includes(c) ? g.filter((x) => x !== c) : [...g, c]);

  const buildDesigner = () => {
    if (!table) throw new Error("Pick a base table");
    const jn = joins.filter((j) => j.table && j.left && j.right).map((j) => ({ type: j.type, table: j.table, left: j.left, right: j.right }));
    const flt = filters.filter((f) => f.column && f.op).map((f) => {
      const op = f.op;
      if (op === "is null" || op === "is not null") return { column: f.column, op };
      if (op === "in" || op === "not in") return { column: f.column, op, value: String(f.value).split(",").map((s) => s.trim()).filter(Boolean) };
      if (op === "between") { const parts = String(f.value).split(",").map((s) => s.trim()); return { column: f.column, op, value: [parts[0], parts[1]] }; }
      return { column: f.column, op, value: f.value };
    });
    const ag = aggs.filter((a) => a.func && (a.column || a.func === "count")).map((a) => ({ func: a.func, column: a.column || "*", alias: a.alias || undefined }));
    return { base_table: table, joins: jn, filters: flt, dimensions: groupBy, aggregations: ag, limit: Number(limit) || null };
  };

  const buildSpec = () => {
    if (isMongo) {
      let filter = null;
      if (filterText.trim()) { try { filter = JSON.parse(filterText); } catch { throw new Error("Filter must be valid JSON"); } }
      return { query_type: "mongo", collection: table, filter };
    }
    if (isGsheet) {
      if (!table) throw new Error("Pick a worksheet");
      return { query_type: "gsheet", worksheet: table };
    }
    if (isRest) return { query_type: "rest" };
    if (mode === "sql") return { query_type: "sql", query: sql };
    return { query_type: "visual", designer: buildDesigner() };
  };

  const doPreview = async () => {
    setBusy(true); setPreview(null);
    try {
      const spec = buildSpec();
      const { data } = await api.post(`/connections/${connId}/preview`, { ...spec, limit: 25 });
      setPreview(data);
      if (!name) setName(table || conn?.name || "Live dataset");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Preview failed"); }
    finally { setBusy(false); }
  };

  const doSave = async () => {
    if (!name) { toast.error("Name the dataset"); return; }
    setBusy(true);
    try {
      const spec = buildSpec();
      if (isEdit) {
        await api.put(`/datasets/${editDs.id}/live-spec`, spec);
        if (name.trim() !== editDs.name || Number(ttl) !== (editDs.cache_ttl_seconds ?? 300)) {
          await api.patch(`/datasets/${editDs.id}`, { name: name.trim(), cache_ttl_seconds: Number(ttl) || 300 });
        }
        toast.success(`Live dataset “${name}” updated`);
      } else {
        const { data } = await api.post("/datasets/live", { connection_id: connId, name, cache_ttl_seconds: Number(ttl) || 300, ...spec });
        toast.success(`Live dataset “${data.name}” created`);
      }
      onOpenChange(false); onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || (isEdit ? "Failed to update dataset" : "Failed to create dataset")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Zap className="w-5 h-5 text-[color:var(--brand-mint)]" />{isEdit ? "Edit live dataset query" : "New live dataset"}</DialogTitle>
          <DialogDescription>{isEdit ? "Redesign this dataset's live query — preview the new results, then save. Dashboards using it pick up the change instantly." : "Connect to a live source and design a query visually or with SQL — data is fetched on demand and cached per your TTL."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <div className="flex items-center justify-between"><Label>Connection</Label>
              {!isEdit && <Button data-testid="live-new-conn-btn" size="sm" variant="ghost" onClick={() => setCreating((v) => !v)} className="text-[color:var(--brand-ocean)] h-7"><Plus className="w-3.5 h-3.5 mr-1" />{creating ? "Cancel" : "New connection"}</Button>}
            </div>
            {creating ? (
              <div className="mt-2"><NewConnectionForm onSaved={(c) => { setConns((p) => [c, ...p]); setConnId(c.id); setTables(c.tables || []); setCreating(false); }} /></div>
            ) : (
              <Select value={connId} onValueChange={setConnId} disabled={isEdit}>
                <SelectTrigger data-testid="live-conn-select" className="mt-1.5"><SelectValue placeholder="Select a saved connection" /></SelectTrigger>
                <SelectContent>{conns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {SOURCE_LABELS[c.db_type] || c.db_type}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>

          {connId && !creating && (
            <>
              {isMongo ? (
                <div className="space-y-3">
                  <div><Label>Collection</Label>
                    <Select value={table} onValueChange={onPickTable}>
                      <SelectTrigger data-testid="live-collection-select" className="mt-1.5"><SelectValue placeholder="Select a collection" /></SelectTrigger>
                      <SelectContent>{tables.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Filter (JSON, optional)</Label><Textarea className="mt-1.5 font-mono text-xs" rows={3} placeholder='{ "status": "active" }' value={filterText} onChange={(e) => setFilterText(e.target.value)} /></div>
                </div>
              ) : isGsheet ? (
                <div><Label>Worksheet</Label>
                  <Select value={table} onValueChange={setTable}>
                    <SelectTrigger data-testid="live-worksheet-select" className="mt-1.5"><SelectValue placeholder="Select a worksheet tab" /></SelectTrigger>
                    <SelectContent>{tables.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-[10.5px] text-[color:var(--ink-3)] mt-1.5">The first row of the worksheet is used as column headers.</p>
                </div>
              ) : isRest ? (
                <p data-testid="rest-dataset-note" className="text-[11px] rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2.5 text-[color:var(--ink-2)]">
                  The API response becomes this dataset — rows are refreshed on every query (respecting the cache TTL below). Use <b>Preview</b> to confirm the columns.
                </p>
              ) : (
                <Tabs value={mode} onValueChange={setMode}>
                  <TabsList className="bg-[color:var(--surface-2)]">
                    <TabsTrigger value="visual" data-testid="live-tab-visual">Visual</TabsTrigger>
                    <TabsTrigger value="sql" data-testid="live-tab-sql">SQL</TabsTrigger>
                  </TabsList>
                  <TabsContent value="visual" className="mt-3 space-y-4">
                    <div><Label>Base table</Label>
                      <Select value={table} onValueChange={onPickTable}>
                        <SelectTrigger data-testid="live-table-select" className="mt-1.5"><SelectValue placeholder="Select a table" /></SelectTrigger>
                        <SelectContent>{tables.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>

                    {table && (
                      <>
                        {/* Joins */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-[color:var(--ink-3)]">Joins (INNER / LEFT)</Label>
                            <Button data-testid="add-join-btn" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={addJoin}><Plus className="w-3 h-3 mr-1" />Join</Button>
                          </div>
                          {joins.map((j, i) => (
                            <div key={j._k || i} data-testid={`join-row-${i}`} className="grid grid-cols-12 gap-1.5 items-center">
                              <Select value={j.type} onValueChange={(v) => updJoin(i, "type", v)}>
                                <SelectTrigger className="col-span-2 h-8 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="inner">INNER</SelectItem><SelectItem value="left">LEFT</SelectItem></SelectContent>
                              </Select>
                              <Select value={j.table} onValueChange={(v) => { updJoin(i, "table", v); fetchCols(v); }}>
                                <SelectTrigger className="col-span-3 h-8 text-xs"><SelectValue placeholder="table" /></SelectTrigger>
                                <SelectContent>{tables.filter((t) => t !== table).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                              </Select>
                              <Select value={j.left} onValueChange={(v) => updJoin(i, "left", v)}>
                                <SelectTrigger className="col-span-3 h-8 text-xs"><SelectValue placeholder="left col" /></SelectTrigger>
                                <SelectContent>{qualifiedColumns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                              </Select>
                              <Select value={j.right} onValueChange={(v) => updJoin(i, "right", v)}>
                                <SelectTrigger className="col-span-3 h-8 text-xs"><SelectValue placeholder="right col" /></SelectTrigger>
                                <SelectContent>{(colsByTable[j.table] || []).map((c) => <SelectItem key={c} value={`${j.table}.${c}`}>{`${j.table}.${c}`}</SelectItem>)}</SelectContent>
                              </Select>
                              <Button size="icon" variant="ghost" className="col-span-1 h-8 w-8 text-[color:var(--danger)]" onClick={() => rmJoin(i)}><X className="w-3.5 h-3.5" /></Button>
                            </div>
                          ))}
                        </div>

                        {/* Filters */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-[color:var(--ink-3)]">Filters</Label>
                            <Button data-testid="add-filter-btn" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={addFilter}><Plus className="w-3 h-3 mr-1" />Filter</Button>
                          </div>
                          {filters.map((f, i) => (
                            <div key={f._k || i} data-testid={`filter-row-${i}`} className="grid grid-cols-12 gap-1.5 items-center">
                              <Select value={f.column} onValueChange={(v) => updFilter(i, "column", v)}>
                                <SelectTrigger className="col-span-4 h-8 text-xs"><SelectValue placeholder="column" /></SelectTrigger>
                                <SelectContent>{qualifiedColumns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                              </Select>
                              <Select value={f.op} onValueChange={(v) => updFilter(i, "op", v)}>
                                <SelectTrigger className="col-span-3 h-8 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>{FILTER_OPS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                              </Select>
                              {(f.op === "is null" || f.op === "is not null")
                                ? <div className="col-span-4 text-[11px] text-[color:var(--ink-3)] px-1">no value</div>
                                : <Input className="col-span-4 h-8 text-xs" placeholder={f.op === "in" || f.op === "not in" ? "a, b, c" : f.op === "between" ? "low, high" : "value"} value={f.value} onChange={(e) => updFilter(i, "value", e.target.value)} />}
                              <Button size="icon" variant="ghost" className="col-span-1 h-8 w-8 text-[color:var(--danger)]" onClick={() => rmFilter(i)}><X className="w-3.5 h-3.5" /></Button>
                            </div>
                          ))}
                        </div>

                        {/* Group by */}
                        <div>
                          <Label className="text-xs text-[color:var(--ink-3)]">Group by ({groupBy.length || "none"})</Label>
                          <div className="flex flex-wrap gap-1.5 mt-1.5 max-h-24 overflow-y-auto">
                            {qualifiedColumns.map((c) => (
                              <button key={c} data-testid={`groupby-${c}`} onClick={() => toggleGroup(c)} className={"text-[11px] px-2 py-1 rounded-full border transition-colors " + (groupBy.includes(c) ? "bg-[color:var(--brand-ocean)] text-white border-transparent" : "bg-[color:var(--surface-2)] text-[color:var(--ink-2)] border-[color:var(--glass-border)]")}>{c}</button>
                            ))}
                          </div>
                        </div>

                        {/* Aggregations */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-[color:var(--ink-3)]">Aggregations</Label>
                            <Button data-testid="add-agg-btn" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={addAgg}><Plus className="w-3 h-3 mr-1" />Measure</Button>
                          </div>
                          {aggs.map((a, i) => (
                            <div key={a._k || i} data-testid={`agg-row-${i}`} className="grid grid-cols-12 gap-1.5 items-center">
                              <Select value={a.func} onValueChange={(v) => updAgg(i, "func", v)}>
                                <SelectTrigger className="col-span-3 h-8 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>{AGG_FUNCS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                              </Select>
                              <Select value={a.column} onValueChange={(v) => updAgg(i, "column", v)}>
                                <SelectTrigger className="col-span-4 h-8 text-xs"><SelectValue placeholder={a.func === "count" ? "* (all rows)" : "column"} /></SelectTrigger>
                                <SelectContent>{qualifiedColumns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                              </Select>
                              <Input className="col-span-4 h-8 text-xs" placeholder="alias (optional)" value={a.alias} onChange={(e) => updAgg(i, "alias", e.target.value)} />
                              <Button size="icon" variant="ghost" className="col-span-1 h-8 w-8 text-[color:var(--danger)]" onClick={() => rmAgg(i)}><X className="w-3.5 h-3.5" /></Button>
                            </div>
                          ))}
                        </div>

                        <div className="w-32"><Label className="text-xs text-[color:var(--ink-3)]">Row limit</Label><Input data-testid="live-limit" type="number" min={1} className="mt-1.5 h-8 text-xs" value={limit} onChange={(e) => setLimit(e.target.value)} /></div>
                      </>
                    )}
                  </TabsContent>
                  <TabsContent value="sql" className="mt-3">
                    <Label>SQL query</Label>
                    <Textarea data-testid="live-sql-input" className="mt-1.5 font-mono text-xs" rows={5} placeholder="SELECT region, SUM(amount) AS total FROM sales GROUP BY region" value={sql} onChange={(e) => setSql(e.target.value)} />
                  </TabsContent>
                </Tabs>
              )}

              <Button data-testid="live-preview-btn" variant="outline" onClick={doPreview} disabled={busy} className="w-full bg-[color:var(--surface)]">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Table2 className="w-4 h-4 mr-1" />}Preview</Button>

              {preview && (
                <div className="rounded-xl border border-[color:var(--glass-border)] overflow-hidden">
                  <div className="px-3 py-2 text-xs text-[color:var(--ink-3)] bg-[color:var(--surface-2)]">{preview.columns.length} columns · showing {preview.rows.length} sample rows (live)</div>
                  <div className="overflow-auto max-h-52">
                    <table className="w-full text-xs"><thead className="bg-[color:var(--surface-2)] sticky top-0"><tr>{preview.columns.map((c) => <th key={c.name} className="text-left px-3 py-1.5 font-medium text-[color:var(--ink-2)] whitespace-nowrap">{c.name}</th>)}</tr></thead>
                      <tbody>{preview.rows.slice(0, 25).map((r, i) => <tr key={`pr-${i}`} className="border-t border-[color:var(--glass-border)]">{preview.columns.map((c) => <td key={c.name} className="px-3 py-1 text-[color:var(--ink-2)] whitespace-nowrap">{String(r[c.name] ?? "—")}</td>)}</tr>)}</tbody>
                    </table>
                  </div>
                </div>
              )}

              {preview?.compiled_sql && (
                <div data-testid="compiled-sql" className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2.5">
                  <div className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)] mb-1">Compiled SQL</div>
                  <code className="text-[11px] font-mono text-[color:var(--ink-2)] break-all whitespace-pre-wrap">{preview.compiled_sql}</code>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><Label>Dataset name</Label><Input data-testid="live-dataset-name" className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} /></div>
                <div><Label>Cache TTL (s)</Label><Input data-testid="live-ttl" type="number" min={5} max={86400} className="mt-1.5" value={ttl} onChange={(e) => setTtl(e.target.value)} /></div>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="live-save-btn" onClick={doSave} disabled={busy || !connId || !preview} className="bg-[color:var(--brand-ocean)] text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}{isEdit ? "Save query changes" : "Create live dataset"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

